import { DEMO_PROJECT } from './demo-project';
import type { GgxTourDefinition } from './tour-types';

export const HOWTO_03: GgxTourDefinition = {
    key: '[HOWTO-03] Running your first evaluation',
    route: '/home',
    project: DEMO_PROJECT,
    estimatedMinutes: 3,
    prerequisite: {
        target: '[test-id="search-button"]',
        message: 'Open the home page, then start this tour.',
    },
    intro: {
        headline: 'Running Your First Evaluation',
        subtitle:
            'GenAI brings new risks such as hallucinations and prompt injections. An ' +
            'evaluation runs your pipeline against a dataset and scores it with a report, so ' +
            'you can measure those risks. This tour runs an accuracy evaluation end to end.',
    },
    outro: {
        title: "That's a wrap!",
        subtitle:
            'You started a simulation, browsed and filtered the report library, picked an ' +
            'accuracy report suited to this pipeline, and submitted the job. It now sits in the ' +
            'queue and runs when resources are free, usually within 5 to 10 minutes for small ' +
            'datasets. You can track it in the Jobs tab.',
        learned: [
            '**Run** on a pipeline opens a simulation.',
            'The **report library** filters down to the right accuracy report.',
            'A submitted job **queues in the Jobs tab** and runs when resources are free.',
        ],
    },
    chapters: [
        {
            name: 'Open the pipeline',
            blurb: 'Search from anywhere and jump straight to it',
            icon: 'map',
            steps: [2, 3, 4],
        },
        {
            name: 'Start an evaluation',
            blurb: 'Open a simulation from Run',
            icon: 'pulse',
            steps: [5, 6],
        },
        {
            name: 'Choose a report',
            blurb: 'Filter and pick the right report',
            icon: 'search',
            steps: [7, 8, 9],
        },
        {
            name: 'Run the job',
            blurb: 'Check the inputs and submit',
            icon: 'compass',
            steps: [10, 11],
        },
    ],
    steps: [
        {
            target: '[test-id="search-button"]',
            title: "Let's open the pipeline we will evaluate",
            whatHappens: 'a search box opens in the middle of the screen.',
            icon: 'search',
            description:
                'We will use <strong>Combined Customer Intent and Response Pipeline</strong>, ' +
                'a customer service chatbot that is already set up. ' +
                'The quickest way to it is the global search. It finds pipelines, prompts, ' +
                'reports and portals by name from any page, and <strong>Ctrl+K</strong> opens ' +
                'it too. Click <strong>Search</strong>.',
            side: 'bottom',
            align: 'end',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
            actionLabel: 'Search',
        },
        {
            target: '[test-id="global-search"] input',
            title: 'Type the name of the chatbot',
            whatHappens:
                'matching objects appear below the box as you type, and the tour moves on when ' +
                'the pipeline is among them.',
            icon: 'search',
            description:
                'Click the tag to copy the name, paste it into the search box, and watch the ' +
                'results narrow down.<br><br>' +
                '<code data-ggx-copy>Combined Customer Intent and Response Pipeline</code>',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetMount: true,
            waitCue: 'Type the name to continue',
        },
        {
            target: '[test-id^="global-search-option-"]',
            targetText: 'Combined Customer Intent and Response Pipeline v1',
            scrollIntoView: true,
            title: 'Pick the pipeline itself',
            whatHappens: "the pipeline's page opens, with a Run button in the top right.",
            icon: 'search',
            description:
                'A few objects share this name. The one we want is the pipeline marked ' +
                '<strong>v1</strong>. Click <strong>Combined Customer Intent and Response ' +
                'Pipeline</strong>.',
            side: 'right',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Combined Customer Intent and Response Pipeline',
        },
        {
            target: '[test-id="run-button"]',
            title: 'Every test starts from Run',
            whatHappens: 'a small menu opens with the ways this pipeline can run.',
            icon: 'pulse',
            description:
                'Run is how you put a pipeline to work. From here you can chat with it live, or ' +
                'send a whole dataset through it in one go. An evaluation is that second kind of ' +
                'run with a report attached, so this is where we start. Click ' +
                '<strong>Run</strong>.',
            side: 'left',
            align: 'start',
            buttons: ['previous', 'close'],
            actionLabel: 'Run',
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id="run-button-option-Simulation"]',
            title: 'We want a Simulation',
            whatHappens: 'the simulation setup opens, where you pick a report and the data to run.',
            icon: 'grid',
            description:
                'A simulation sends many questions through the pipeline at once and scores the ' +
                'answers automatically, with nobody typing them by hand. That is what makes it ' +
                'an objective test of how the pipeline behaves. Click ' +
                '<strong>Simulation</strong>.',
            side: 'left',
            align: 'start',
            buttons: ['previous', 'close'],
            actionLabel: 'Simulation',
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id="report-selection"]',
            title: 'Now, what should we measure?',
            whatHappens: 'the report library opens with every report you can use.',
            icon: 'search',
            description:
                'An evaluation has three parts. Your input data goes into the pipeline, the ' +
                'pipeline writes its answers, and a report turns those answers into scores. GGX ' +
                'ships a library of ready-made reports for risks like accuracy, toxicity and ' +
                'prompt injection. Click <strong>Select a report to run</strong> to open it.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'close'],
            actionLabel: 'the report field',
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id="risk-type-filter-item-Accuracy"]',
            title: "Let's narrow it down to accuracy",
            whatHappens: 'the list shrinks to only the reports that measure accuracy.',
            icon: 'list',
            description:
                'The library is long, so filters help you find the right report. Each filter is ' +
                'a type of risk. We care about whether the chatbot gives correct answers, and ' +
                'that is accuracy. Click <strong>Accuracy</strong> in the filter list.',
            side: 'right',
            align: 'start',
            buttons: ['previous', 'close'],
            actionLabel: 'Accuracy',
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id="report-card-Response Accuracy (Credit  Card Queries)"]',
            title: 'This one fits our chatbot',
            whatHappens: 'the report is added, and GGX picks a matching sample dataset for you.',
            icon: 'grid',
            description:
                'Some accuracy reports work with any pipeline. Others are built for a specific ' +
                'setup. <strong>Response Accuracy (Credit Card Queries)</strong> checks whether ' +
                'each answer is backed by the facts the pipeline looked up in its knowledge ' +
                'base, so it only makes sense for a pipeline that does that lookup (called RAG). ' +
                'Ours does, so it is the right choice. Click the card to select it.',
            side: 'left',
            align: 'start',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id="eval-data-options"]',
            title: 'Your input data is already chosen',
            whatHappens: 'the recommended dataset stays selected for this run.',
            icon: 'clock',
            description:
                'Each report comes with a recommended dataset of test questions, so you can run ' +
                'an evaluation right away without preparing anything. You can swap in your own ' +
                'data later; the "Using your own data in Evals" tour shows how. For now the ' +
                'sample data is exactly what we need, so click Continue.',
            side: 'top',
            align: 'start',
        },
        {
            target: '[test-id="run-job"]',
            title: "All set. Let's run it",
            whatHappens: 'the job goes into the queue and starts running in the background.',
            icon: 'compass',
            description:
                'You have a pipeline, test data and a report that scores the answers. When you ' +
                'submit, GGX sends every question through the pipeline, collects the replies and ' +
                'scores them. Small datasets usually take 5 to 10 minutes, and you can watch ' +
                'progress in the Jobs tab. Click <strong>Run Job</strong>.',
            side: 'left',
            align: 'center',
            buttons: ['previous', 'close'],
            actionLabel: 'Run Job',
            advanceOnTargetClick: true,
        },
    ],
};
